import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { US_STATES } from '../../constants/options';
import { updateDemoEnvironment } from '../../services/api';
import type { DemoEnvironmentResponse } from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { validateCcn } from '../../utils/ccnValidation';
import { validateNpi } from '../../utils/npiValidation';
import {
  DEFAULT_DEMO_FF_TEMPLATE_AGENCY,
  DEMO_FALLBACK_ADDRESS,
  DEMO_FALLBACK_BILLING_CONTACT_FIRST_NAME,
  DEMO_FALLBACK_BILLING_CONTACT_LAST_NAME,
  DEMO_FALLBACK_BILLING_CONTACT_PHONE,
  DEMO_FALLBACK_CCN,
  DEMO_FALLBACK_CMS_BRANCH_ID,
  DEMO_FALLBACK_CMS_QIES_AGENCY_ID,
  DEMO_FALLBACK_CITY,
  DEMO_FALLBACK_EIN,
  DEMO_FALLBACK_NPI,
  DEMO_FALLBACK_PHONE,
  DEMO_FALLBACK_STATE,
  DEMO_FALLBACK_ZIP,
} from './demoAgencySetupDefaults';
import { inferDemoAgencySetupStageLabel } from './demoAgencySetupUi';
import { formatPullSummary, usePullKinnser } from './usePullKinnser';
import {
  DEMO_REFRESH_TIME_LABEL,
  distinctSystemName,
  resolveSettings,
  type ResolvedDemoEnvironmentSettings,
} from './demoEnvironmentStatus';

/**
 * Edit one agency's saved demo settings.
 *
 * There is no add mode: every Kinnser agency is already listed, and saving an
 * agency's settings for the first time is what puts it in "My sandboxes".
 *
 * An untouched agency arrives with the backend's default settings, which include
 * the three default team emails. That matters — an empty list reads as "invite
 * nobody" and skips the invite, chart and QA seeding while the run still reports
 * success.
 */

interface Props {
  /** The agency being edited; the dialog is closed when null. */
  environment: DemoEnvironmentResponse | null;
  onClose: () => void;
  onSaved: () => void;
  /** After a successful pull, so the list's clinic count is current. */
  onPulled?: () => void;
}

// The invite step fails several minutes into a run, so a typo caught here saves
// the whole round trip.
const DEMO_TEAM_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The server applies the same rules. A value that fails them would be replaced
// with the demo fake at run time with no error, so it is caught here instead.
// NPI uses the app's shared clinic NPI check, check digit included.
const digitCount = (value: string) => value.replace(/\D/g, '').length;
const EIN_DIGITS = 9;
const PHONE_DIGITS = 10;
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;
// The lengths the claim builder cuts the billing provider's street and city to.
const ADDRESS_MAX_LENGTH = 55;
const CITY_MAX_LENGTH = 30;
// A Select item cannot have an empty value, so "no state" needs its own.
const NO_STATE = 'none';

export const DemoEnvironmentFormDialog: React.FC<Props> = ({
  environment,
  onClose,
  onSaved,
  onPulled,
}) => {
  const refreshScheduled = useMemo(
    () => inferDemoAgencySetupStageLabel() === 'demo',
    []
  );
  const [settings, setSettings] = useState<ResolvedDemoEnvironmentSettings>(
    resolveSettings(undefined, DEFAULT_DEMO_FF_TEMPLATE_AGENCY)
  );
  const [emailsText, setEmailsText] = useState('');
  const [npi, setNpi] = useState('');
  const [ein, setEin] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [usState, setUsState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [ccn, setCcn] = useState('');
  const [qiesAgencyId, setQiesAgencyId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [autoMrn, setAutoMrn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    pull,
    pulling,
    result: pullResult,
    error: pullError,
    reset: resetPull,
  } = usePullKinnser();
  // A pull just made here, else the one saved on the config: the same pull
  // either way, so the line reads the same after a save and a reopen.
  const latestPull = pullResult ?? environment?.kinnser_pull ?? null;

  useEffect(() => {
    if (!environment) return;
    resetPull();
    const resolved = resolveSettings(
      environment.settings,
      DEFAULT_DEMO_FF_TEMPLATE_AGENCY
    );
    setError(null);
    setSettings(resolved);
    // The textarea is what save() reads, so it must hold the saved emails.
    setEmailsText(resolved.demo_team_member_emails.join('\n'));
    setNpi(environment.profile?.npi ?? '');
    setEin(environment.profile?.ein ?? '');
    setPhone(environment.profile?.phone ?? '');
    setAddress(environment.profile?.address ?? '');
    setCity(environment.profile?.city ?? '');
    setUsState(environment.profile?.state ?? '');
    setZipCode(environment.profile?.zip_code ?? '');
    setCcn(environment.profile?.hha_agency_id ?? '');
    setQiesAgencyId(environment.profile?.cms_qies_agency_id ?? '');
    setBranchId(environment.profile?.cms_branch_id ?? '');
    setContactFirstName(environment.profile?.billing_contact_first_name ?? '');
    setContactLastName(environment.profile?.billing_contact_last_name ?? '');
    setContactPhone(environment.profile?.billing_contact_phone ?? '');
    setAutoMrn(environment.profile?.auto_mrn ?? false);
  }, [environment, resetPull]);

  const invalidEmails = emailsText
    .split('\n')
    .map(line => line.trim())
    .filter(line => line && !DEMO_TEAM_EMAIL_PATTERN.test(line));

  // Strip formatting only, so "N/A" is an error rather than an empty field.
  const npiError = validateNpi(npi.replace(/[\s-]/g, ''));
  const einInvalid = ein.trim() !== '' && digitCount(ein) !== EIN_DIGITS;
  const phoneInvalid =
    phone.trim() !== '' && digitCount(phone) !== PHONE_DIGITS;
  const addressTooLong = address.trim().length > ADDRESS_MAX_LENGTH;
  const cityTooLong = city.trim().length > CITY_MAX_LENGTH;
  const zipInvalid = zipCode.trim() !== '' && !ZIP_PATTERN.test(zipCode.trim());
  const ccnError = validateCcn(ccn);
  const contactPhoneInvalid =
    contactPhone.trim() !== '' && digitCount(contactPhone) !== PHONE_DIGITS;
  const profileInvalid =
    npiError !== null ||
    ccnError !== null ||
    einInvalid ||
    phoneInvalid ||
    contactPhoneInvalid ||
    addressTooLong ||
    cityTooLong ||
    zipInvalid;

  const save = async () => {
    if (!environment) return;
    setSaving(true);
    setError(null);
    // One email per line, blanks dropped: pasting a column out of a spreadsheet
    // is how these arrive.
    const emails = emailsText
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);
    try {
      await updateDemoEnvironment({
        path: { agency_id: environment.agency_id },
        // The server replaces the whole saved block, so start from what it sent:
        // a setting this dialog does not edit keeps its saved value.
        body: {
          settings: {
            ...environment.settings,
            ...settings,
            demo_team_member_emails: emails,
          },
          // Empty means "use demo data"; the server stores it as absent.
          profile: {
            npi: npi.trim() || null,
            ein: ein.trim() || null,
            phone: phone.trim() || null,
            address: address.trim() || null,
            city: city.trim() || null,
            state: usState || null,
            zip_code: zipCode.trim() || null,
            hha_agency_id: ccn.trim() || null,
            cms_qies_agency_id: qiesAgencyId.trim() || null,
            cms_branch_id: branchId.trim() || null,
            billing_contact_first_name: contactFirstName.trim() || null,
            billing_contact_last_name: contactLastName.trim() || null,
            billing_contact_phone: contactPhone.trim() || null,
            auto_mrn: autoMrn,
          },
        },
        throwOnError: true,
      });
      onSaved();
    } catch (caught) {
      console.error('Failed to save demo settings', caught);
      setError(
        getBusinessErrorMessage(caught) ??
          'Unable to save these settings. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const systemName =
    environment &&
    distinctSystemName(
      environment.agency_written_name,
      environment.agency_system_name
    );

  return (
    <Dialog
      open={environment !== null}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {environment?.agency_written_name ||
              environment?.agency_system_name}
            {systemName && (
              <span className='ml-2 font-mono text-sm font-normal text-muted-foreground'>
                {systemName}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Used every time you run this sandbox.
          </DialogDescription>
        </DialogHeader>

        {/* Every tab stays mounted: Save reads both. The tabs share one grid
            cell and the inactive one is invisible rather than hidden, so the
            dialog keeps the taller tab's height when you switch. */}
        <Tabs defaultValue='agency-data'>
          <TabsList className='w-fit'>
            <TabsTrigger value='agency-data'>Agency Data</TabsTrigger>
            <TabsTrigger value='settings'>Settings</TabsTrigger>
          </TabsList>

          <div className='mt-4 grid'>
            <TabsContent
              value='agency-data'
              forceMount
              className='col-start-1 row-start-1 mt-0 space-y-4 data-[state=inactive]:invisible'
            >
              <fieldset className='space-y-2'>
                <legend className='text-sm font-medium'>Kinnser data</legend>
                <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
                  <p className='text-xs text-muted-foreground'>
                    {latestPull
                      ? formatPullSummary(latestPull)
                      : 'Not pulled yet. Pull before the first run.'}
                  </p>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    disabled={!environment || pulling || saving}
                    onClick={async () => {
                      if (!environment) return;
                      if (await pull(environment.agency_id)) onPulled?.();
                    }}
                  >
                    {pulling && (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    )}
                    Pull from Kinnser
                  </Button>
                </div>
                {pullError && (
                  <p className='text-sm text-red-700'>{pullError}</p>
                )}
              </fieldset>

              <div className='grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-2'>
                <label className='flex flex-col gap-1'>
                  <span className='text-sm font-medium'>NPI</span>
                  <Input
                    value={npi}
                    onChange={event => setNpi(event.target.value)}
                    placeholder={DEMO_FALLBACK_NPI}
                    inputMode='numeric'
                    aria-invalid={npiError !== null}
                  />
                  {npiError && (
                    <span className='text-xs text-red-700'>{npiError}</span>
                  )}
                </label>
                <label className='flex flex-col gap-1'>
                  <span className='text-sm font-medium'>EIN</span>
                  <Input
                    value={ein}
                    onChange={event => setEin(event.target.value)}
                    placeholder={DEMO_FALLBACK_EIN}
                    aria-invalid={einInvalid}
                  />
                  {einInvalid && (
                    <span className='text-xs text-red-700'>
                      EIN must be 9 digits.
                    </span>
                  )}
                </label>
                <label className='flex flex-col gap-1'>
                  <span className='text-sm font-medium'>Phone</span>
                  <Input
                    value={phone}
                    onChange={event => setPhone(event.target.value)}
                    placeholder={DEMO_FALLBACK_PHONE}
                    inputMode='tel'
                    aria-invalid={phoneInvalid}
                  />
                  {phoneInvalid && (
                    <span className='text-xs text-red-700'>
                      Phone must be 10 digits.
                    </span>
                  )}
                </label>
              </div>

              <div className='grid grid-cols-1 gap-3 sm:grid-cols-4'>
                <label className='flex flex-col gap-1 sm:col-span-4'>
                  <span className='text-sm font-medium'>Street address</span>
                  <Input
                    value={address}
                    onChange={event => setAddress(event.target.value)}
                    placeholder={DEMO_FALLBACK_ADDRESS}
                    aria-invalid={addressTooLong}
                  />
                  {addressTooLong && (
                    <span className='text-xs text-red-700'>
                      Street address must be {ADDRESS_MAX_LENGTH} characters or
                      fewer.
                    </span>
                  )}
                </label>
                <label className='flex flex-col gap-1 sm:col-span-2'>
                  <span className='text-sm font-medium'>City</span>
                  <Input
                    value={city}
                    onChange={event => setCity(event.target.value)}
                    placeholder={DEMO_FALLBACK_CITY}
                    aria-invalid={cityTooLong}
                  />
                  {cityTooLong && (
                    <span className='text-xs text-red-700'>
                      City must be {CITY_MAX_LENGTH} characters or fewer.
                    </span>
                  )}
                </label>
                <div className='flex flex-col gap-1'>
                  <span id='demo-profile-state' className='text-sm font-medium'>
                    State
                  </span>
                  <Select
                    value={usState || undefined}
                    onValueChange={value =>
                      setUsState(value === NO_STATE ? '' : value)
                    }
                  >
                    <SelectTrigger aria-labelledby='demo-profile-state'>
                      <SelectValue placeholder={DEMO_FALLBACK_STATE} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_STATE}>Use demo data</SelectItem>
                      {US_STATES.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className='flex flex-col gap-1'>
                  <span className='text-sm font-medium'>ZIP</span>
                  <Input
                    value={zipCode}
                    onChange={event => setZipCode(event.target.value)}
                    placeholder={DEMO_FALLBACK_ZIP}
                    inputMode='numeric'
                    aria-invalid={zipInvalid}
                  />
                  {zipInvalid && (
                    <span className='text-xs text-red-700'>
                      ZIP must be 12345 or 12345-6789.
                    </span>
                  )}
                </label>
              </div>

              <fieldset className='space-y-2 border-t pt-4'>
                <legend className='text-sm font-medium'>OASIS</legend>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                  <label className='flex flex-col gap-1'>
                    <span className='text-sm font-medium'>CCN / PTAN</span>
                    <Input
                      value={ccn}
                      onChange={event => setCcn(event.target.value)}
                      placeholder={DEMO_FALLBACK_CCN}
                      aria-invalid={ccnError !== null}
                    />
                    {ccnError && (
                      <span className='text-xs text-red-700'>{ccnError}</span>
                    )}
                  </label>
                  <label className='flex flex-col gap-1'>
                    <span className='text-sm font-medium'>HHA Agency ID</span>
                    <Input
                      value={qiesAgencyId}
                      onChange={event => setQiesAgencyId(event.target.value)}
                      placeholder={DEMO_FALLBACK_CMS_QIES_AGENCY_ID}
                      maxLength={16}
                    />
                  </label>
                  <label className='flex flex-col gap-1'>
                    <span className='text-sm font-medium'>Branch ID</span>
                    <Input
                      value={branchId}
                      onChange={event => setBranchId(event.target.value)}
                      placeholder={DEMO_FALLBACK_CMS_BRANCH_ID}
                      maxLength={10}
                    />
                  </label>
                </div>
                <p className='text-xs text-muted-foreground'>
                  HHA Agency ID is HHA_AGENCY_ID in the OASIS export. Branch ID
                  is N when the clinic has no branches.
                </p>
              </fieldset>

              <fieldset className='space-y-2 border-t pt-4'>
                <legend className='text-sm font-medium'>Billing contact</legend>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                  <label className='flex flex-col gap-1'>
                    <span className='text-sm font-medium'>First name</span>
                    <Input
                      value={contactFirstName}
                      onChange={event =>
                        setContactFirstName(event.target.value)
                      }
                      placeholder={DEMO_FALLBACK_BILLING_CONTACT_FIRST_NAME}
                      maxLength={35}
                    />
                  </label>
                  <label className='flex flex-col gap-1'>
                    <span className='text-sm font-medium'>Last name</span>
                    <Input
                      value={contactLastName}
                      onChange={event => setContactLastName(event.target.value)}
                      placeholder={DEMO_FALLBACK_BILLING_CONTACT_LAST_NAME}
                      maxLength={60}
                    />
                  </label>
                  <label className='flex flex-col gap-1'>
                    <span className='text-sm font-medium'>
                      Billing contact phone
                    </span>
                    <Input
                      value={contactPhone}
                      onChange={event => setContactPhone(event.target.value)}
                      placeholder={DEMO_FALLBACK_BILLING_CONTACT_PHONE}
                      inputMode='tel'
                      aria-invalid={contactPhoneInvalid}
                    />
                    {contactPhoneInvalid && (
                      <span className='text-xs text-red-700'>
                        Phone must be 10 digits.
                      </span>
                    )}
                  </label>
                </div>
                <p className='text-xs text-muted-foreground'>
                  Who payers call about claims.
                </p>
              </fieldset>

              <fieldset className='space-y-2 border-t pt-4'>
                <legend className='text-sm font-medium'>Clinic settings</legend>
                <label className='flex items-center gap-2'>
                  <Checkbox
                    checked={autoMrn}
                    onCheckedChange={checked => setAutoMrn(checked === true)}
                  />
                  <span className='text-sm'>Auto-assign MRNs</span>
                </label>
                <p className='text-xs text-muted-foreground'>
                  Roger numbers new patients. Off, each intake needs an MRN.
                </p>
              </fieldset>
            </TabsContent>

            <TabsContent
              value='settings'
              forceMount
              className='col-start-1 row-start-1 mt-0 space-y-4 data-[state=inactive]:invisible'
            >
              <label className='flex items-center gap-2'>
                <Checkbox
                  checked={settings.include_coding}
                  onCheckedChange={checked =>
                    setSettings(current => ({
                      ...current,
                      include_coding: checked === true,
                    }))
                  }
                />
                <span className='text-sm'>Include RogerDoc coding data</span>
              </label>

              {/* Off and disabled where the nightly job does not run. Only the
                  display changes: the saved value is still sent back on save. */}
              <label className='flex items-center gap-2'>
                <Checkbox
                  checked={refreshScheduled && settings.refresh_nightly}
                  disabled={!refreshScheduled}
                  onCheckedChange={checked =>
                    setSettings(current => ({
                      ...current,
                      refresh_nightly: checked === true,
                    }))
                  }
                />
                <span
                  className={`text-sm ${refreshScheduled ? '' : 'text-muted-foreground'}`}
                >
                  Refresh nightly at {DEMO_REFRESH_TIME_LABEL}
                  {!refreshScheduled && ' (demo environment only)'}
                </span>
              </label>

              <label className='flex flex-col gap-1'>
                <span className='text-sm font-medium'>
                  Feature-flag template agency
                </span>
                <Input
                  value={settings.demo_ff_template_agency}
                  onChange={event =>
                    setSettings(current => ({
                      ...current,
                      demo_ff_template_agency: event.target.value,
                    }))
                  }
                />
              </label>

              <label className='flex flex-col gap-1'>
                <span className='text-sm font-medium'>
                  Team member emails, one per line
                </span>
                <Textarea
                  rows={4}
                  value={emailsText}
                  onChange={event => setEmailsText(event.target.value)}
                  placeholder='someone@rogerhealthcare.com'
                />
              </label>

              {invalidEmails.length > 0 && (
                <p className='text-sm text-red-700'>
                  Not a valid email: {invalidEmails.join(', ')}
                </p>
              )}
            </TabsContent>
          </div>
        </Tabs>

        {error && <p className='text-sm text-red-700'>{error}</p>}

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={() => void save()}
            disabled={
              saving || pulling || invalidEmails.length > 0 || profileInvalid
            }
          >
            {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DemoEnvironmentFormDialog;
